import React, { useMemo, useState } from 'react';
import isEmpty from 'lodash/isEmpty';
import parse from 'html-react-parser';
import moment from 'moment';

import { Modal } from 'antd';
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl';
import { connect } from 'react-redux';

import { IAccount, ICondoUnit, IDetails } from '@share/common-types';
import { getFullAddress } from '@utils';
import { renderNumber, getNights, to12Hour, getSelectedCurrency, RootState } from '@share/utils';
import { Currency, SaveProperty, SavePropertyTypes } from '@components';
import { ILoginState } from '@share/store/slices';
import { RefundableEnum } from '@common-types';

import {
  ManDarkSvg,
  BedSmallSvg,
  BathSvg,
  InfoSvg,
  CheckMarkSvg,
  StarSvg,
  ExclamationGreySvg,
} from '@assets';

import './style.scss';

const CarouselArrowLeft = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11.25 4.5L6.75 9L11.25 13.5" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const CarouselArrowRight = () => (
  <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6.75 4.5L11.25 9L6.75 13.5" stroke="#333" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface IMapStateToProps {
  loginStore: ILoginState;
}

interface IProps extends WrappedComponentProps, IMapStateToProps {
  condo: IDetails | undefined;
  unit: ICondoUnit;
  visible: boolean;
  adultsCount: number | null;
  childrenCount: number | null;
  walletPrices: any;
  certificateApplied?: boolean;
  savings?: number | null;
  savingsPercent?: number;
  onReserve?: () => void;
  onCancel: (e?: React.MouseEvent<HTMLElement, MouseEvent>) => void;
}

const ZERO = 0;
const ONE = 1;
const NO = 'No';

function ModalUnitDetailsNewComponent(props: IProps) {
  const {
    visible,
    onCancel,
    onReserve,
    condo = {} as IDetails,
    adultsCount,
    childrenCount,
    walletPrices,
    certificateApplied,
    savings,
    savingsPercent = 0,
    intl,
    loginStore,
    unit = {} as ICondoUnit,
  } = props;

  const {
    id,
    title,
    media = [],
    fullAddress,
    address,
    city,
    country,
    rating,
    thumb,
    amenities = [],
    facilities = [],
    urgentInfo,
    mandatoryFees = [],
    fees = [],
    housekeepingFees = [],
    checkInCheckOutInfo,
    savedProperty,
  } = condo;

  const {
    name,
    bedroomsCount,
    bathroomsCount,
    dateRange,
    maxOccupancy,
    privacyOccupancy,
    sleepingArrangements,
    refundability,
    cancellationPolicyText,
    roomAmenities,
    coolingSystems,
    kitchenEquipments,
    price,
    pricePerNight,
    publicPrice,
  } = unit;

  const { account } = loginStore;
  const currency = useMemo(
    () => getSelectedCurrency(account as IAccount),
    [account],
  );

  const [activeIndex, setActiveIndex] = useState(0);

  const guestsCount = (adultsCount || 0) + (childrenCount || 0);

  const nights = useMemo(() => {
    if (dateRange?.from && dateRange?.to) {
      return getNights(dateRange.from, dateRange.to) || ONE;
    }
    return ONE;
  }, [dateRange]);

  const computedPricePerNight = useMemo(() => {
    if (pricePerNight && pricePerNight > 0) return pricePerNight;
    if (price && nights) return price / nights;
    return 0;
  }, [pricePerNight, price, nights]);

  const taxesAndFees = useMemo(() => {
    const base = computedPricePerNight * nights;
    return price && base ? Math.max(price - base, 0) : 0;
  }, [computedPricePerNight, nights, price]);

  const isRefundable = refundability === RefundableEnum.Refundable;
  const isNonRefundable = refundability === RefundableEnum.Nonrefundable;

  const mediaList = useMemo(() => (Array.isArray(media) ? media : []), [media]);

  const allAmenities = useMemo(() => {
    const fromCondo = [
      ...(amenities || []).map((a) => a.name),
      ...(facilities || []).map((a) => a.name),
    ];
    const fromUnit = (roomAmenities || [])
      .filter((a) => a.details?.[ZERO] !== NO)
      .map((a) => a.name);
    const fromCooling = coolingSystems || [];
    const fromKitchen = (kitchenEquipments || [])
      .filter((a) => a.details?.[ZERO] !== NO)
      .map((a) => a.name);

    const merged = [...fromCondo, ...fromUnit, ...fromCooling, ...fromKitchen]
      .filter(Boolean);

    return Array.from(new Set(merged));
  }, [amenities, facilities, roomAmenities, coolingSystems, kitchenEquipments]);

  const knowBeforeYouGoMessages = useMemo(() => {
    if (!urgentInfo) return [] as string[];
    const m: string[] = [];
    (urgentInfo.generalUrgentInfoMessages || []).forEach((i) => i && m.push(i));
    (urgentInfo.generalUrgentInfoForNonMembersMessages || []).forEach(
      (i) => i && m.push(i),
    );
    if (urgentInfo.occupancyRestrictions) m.push(urgentInfo.occupancyRestrictions);
    if (urgentInfo.oneInXRuleApplies) m.push(urgentInfo.oneInXRuleApplies);
    if (urgentInfo.separateSwimmingPoolForGenders)
      m.push(intl.formatMessage({ id: 'separate.swimming.pool.for.genders' }));
    if (urgentInfo.alcoholRestricted)
      m.push(intl.formatMessage({ id: 'alcohol.restricted' }));
    if (urgentInfo.alcoholServedOnsite)
      m.push(intl.formatMessage({ id: 'alcohol.served.onsite' }));
    if (urgentInfo.resortStayRestrictionsInfo) m.push(urgentInfo.resortStayRestrictionsInfo);
    return m;
  }, [urgentInfo, intl]);

  const additionalFees = useMemo(() => {
    const list: { label: string; value: string }[] = [];

    (mandatoryFees || [])
      .filter((f) => f && Object.keys(f).length > 0)
      .forEach((f) => {
        if (f.infoMessage || f.additionalInfoMessage) {
          list.push({
            label: intl.formatMessage({ id: 'resort.fee' }),
            value: [f.infoMessage, f.additionalInfoMessage].filter(Boolean).join(' · '),
          });
        }
      });

    (fees || [])
      .filter((f) => f && Object.keys(f).length > 0)
      .forEach((f) => {
        if (f.infoMessage || f.additionalInfoMessage) {
          list.push({
            label: intl.formatMessage({ id: 'utilities' }),
            value: [f.infoMessage, f.additionalInfoMessage].filter(Boolean).join(' · '),
          });
        }
      });

    (housekeepingFees || [])
      .filter((f) => f && Object.keys(f).length > 0)
      .forEach((f) => {
        const v = [f.feeSchedule, f.infoMessage, f.additionalInfoMessage]
          .filter(Boolean)
          .join(' · ');
        if (v) {
          list.push({
            label: intl.formatMessage({ id: 'housekeeping' }),
            value: v,
          });
        }
      });

    return list;
  }, [mandatoryFees, fees, housekeepingFees, intl]);

  const bedsCount = (sleepingArrangements || []).length;
  const bedsDescription = (sleepingArrangements || []).join(', ');

  const checkInDate = dateRange?.from;
  const checkOutDate = dateRange?.to;
  const checkInTime = checkInCheckOutInfo?.checkInTime;
  const checkOutTime = checkInCheckOutInfo?.checkOutTime;

  const formattedAddress = fullAddress || getFullAddress(address, city, '', '', country).join(', ');

  const showStars = !!rating && rating.value > 0;

  const handlePrev = () => {
    if (!mediaList.length) return;
    setActiveIndex((i) => (i - 1 + mediaList.length) % mediaList.length);
  };

  const handleNext = () => {
    if (!mediaList.length) return;
    setActiveIndex((i) => (i + 1) % mediaList.length);
  };

  const handleReserve = () => {
    if (onReserve) onReserve();
  };

  const renderStars = (count: number) => {
    return (
      <span className="modal-unit-details-new__stars">
        {Array.from({ length: count }).map((_v, i) => (
          <StarSvg key={i} />
        ))}
      </span>
    );
  };

  const currentMedia = mediaList[activeIndex];
  const currentTag = currentMedia?.tags?.[0] || currentMedia?.description;

  const hasGlance = guestsCount > 0 || bedsCount > 0 || bedroomsCount > 0 || bathroomsCount > 0 || maxOccupancy > 0 || privacyOccupancy > 0 || !!name;
  const hasDates = !!checkInDate && !!checkOutDate;
  const hasAmenities = allAmenities.length > 0;
  const hasPriceBreakdown = !!price;
  const hasKBYG = knowBeforeYouGoMessages.length > 0;
  const hasFees = additionalFees.length > 0;

  return (
    <Modal
      className="modal-unit-details-new"
      visible={visible}
      footer={null}
      onCancel={onCancel}
      destroyOnClose
      centered={false}
    >
      <div className="modal-unit-details-new__container">
        <div className="modal-unit-details-new__scroll">

          {(name || title) && (
            <div className="modal-unit-details-new__header">
              <div className="modal-unit-details-new__header-top">
                <div>
                  {name && <h4 className="modal-unit-details-new__title">{name}</h4>}
                  <div className="modal-unit-details-new__badges">
                    {showStars && renderStars(Math.round(rating!.value))}
                    {!certificateApplied && isNonRefundable && (
                      <span className="modal-unit-details-new__badge non-refundable">
                        <FormattedMessage id="non.refundable" />
                      </span>
                    )}
                    {!certificateApplied && isRefundable && (
                      <span className="modal-unit-details-new__badge refundable">
                        <FormattedMessage id="refundable" />
                      </span>
                    )}
                    {!!savingsPercent && savingsPercent > 0 && (
                      <span className="modal-unit-details-new__badge save">
                        <FormattedMessage id="save" /> {savingsPercent}%
                      </span>
                    )}
                  </div>
                  <div className="modal-unit-details-new__resort-line">
                    {title && (
                      <span className="modal-unit-details-new__resort-name">{title}</span>
                    )}
                    {title && formattedAddress && (
                      <span className="modal-unit-details-new__resort-sep">·</span>
                    )}
                    {formattedAddress && (
                      <span className="modal-unit-details-new__resort-location">{formattedAddress}</span>
                    )}
                  </div>
                </div>
                {id && (
                  <div className="modal-unit-details-new__save-icon">
                    <SaveProperty
                      isSaved={!!savedProperty}
                      id={id}
                      title={title}
                      thumb={thumb}
                      images={mediaList}
                      starRating={rating?.value || 0}
                      reviewsCount={rating?.reviewsCount || 0}
                      fullAddress={formattedAddress}
                      type={SavePropertyTypes.Condo}
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {mediaList.length > 0 && (
            <>
              <div className="modal-unit-details-new__carousel">
                {currentMedia?.url && (
                  <img src={currentMedia.url} alt={currentMedia?.description || ''} />
                )}
                {mediaList.length > 1 && (
                  <>
                    <button
                      className="modal-unit-details-new__carousel-nav prev"
                      onClick={handlePrev}
                      aria-label="previous"
                    >
                      <CarouselArrowLeft />
                    </button>
                    <button
                      className="modal-unit-details-new__carousel-nav next"
                      onClick={handleNext}
                      aria-label="next"
                    >
                      <CarouselArrowRight />
                    </button>
                  </>
                )}
                {currentTag && (
                  <div className="modal-unit-details-new__caption">{currentTag}</div>
                )}
                <div className="modal-unit-details-new__counter">
                  {activeIndex + 1} / {mediaList.length}
                </div>
              </div>
              {mediaList.length > 1 && (
                <div className="modal-unit-details-new__thumbs">
                  {mediaList.map((m, i) => (
                    <div
                      key={`thumb-${i}`}
                      className={`modal-unit-details-new__thumb ${i === activeIndex ? 'active' : ''}`}
                      onClick={() => setActiveIndex(i)}
                    >
                      {m?.url && <img src={m.url} alt={m?.description || ''} />}
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          {hasDates && (
            <div className="modal-unit-details-new__section">
              <h5 className="modal-unit-details-new__section-title">
                <FormattedMessage id="stay.dates" />
              </h5>
              <div className="modal-unit-details-new__stay-dates">
                <div className="modal-unit-details-new__stay-date-col">
                  <div className="modal-unit-details-new__stay-date-day">
                    {moment(checkInDate).format('MMM D')}
                  </div>
                  <div className="modal-unit-details-new__stay-date-time">
                    {moment(checkInDate).format('ddd')}
                    {checkInTime ? ` · ${to12Hour(checkInTime)}` : ''}
                  </div>
                </div>
                <div className="modal-unit-details-new__stay-date-sep">
                  <span>
                    <FormattedMessage id="nights" values={{ count: nights }} />
                  </span>
                </div>
                <div className="modal-unit-details-new__stay-date-col right">
                  <div className="modal-unit-details-new__stay-date-day">
                    {moment(checkOutDate).format('MMM D')}
                  </div>
                  <div className="modal-unit-details-new__stay-date-time">
                    {moment(checkOutDate).format('ddd')}
                    {checkOutTime ? ` · ${to12Hour(checkOutTime)}` : ''}
                  </div>
                </div>
              </div>
            </div>
          )}

          {hasGlance && (
            <div className="modal-unit-details-new__section">
              <h5 className="modal-unit-details-new__section-title">
                <FormattedMessage id="unit.at.a.glance" />
              </h5>
              <div className="modal-unit-details-new__glance">
                {guestsCount > 0 && (
                  <div className="modal-unit-details-new__glance-card">
                    <div className="modal-unit-details-new__glance-icon">
                      <ManDarkSvg />
                    </div>
                    <div className="modal-unit-details-new__glance-main">
                      <FormattedMessage id="guests.count" values={{ count: guestsCount }} />
                    </div>
                    <div className="modal-unit-details-new__glance-sub">
                      <FormattedMessage id="selected" />
                    </div>
                  </div>
                )}
                {maxOccupancy > 0 && (
                  <div className="modal-unit-details-new__glance-card">
                    <div className="modal-unit-details-new__glance-icon">
                      <ManDarkSvg />
                    </div>
                    <div className="modal-unit-details-new__glance-main">
                      <FormattedMessage id="guests.count" values={{ count: maxOccupancy }} />
                    </div>
                    <div className="modal-unit-details-new__glance-sub">
                      <FormattedMessage id="max.occupancy" />
                    </div>
                  </div>
                )}
                {privacyOccupancy > 0 && (
                  <div className="modal-unit-details-new__glance-card">
                    <div className="modal-unit-details-new__glance-icon">
                      <ManDarkSvg />
                    </div>
                    <div className="modal-unit-details-new__glance-main">
                      <FormattedMessage id="guests.count" values={{ count: privacyOccupancy }} />
                    </div>
                    <div className="modal-unit-details-new__glance-sub">
                      <FormattedMessage id="private.occupancy" />
                    </div>
                  </div>
                )}
                {bedsCount > 0 && (
                  <div className="modal-unit-details-new__glance-card">
                    <div className="modal-unit-details-new__glance-icon">
                      <BedSmallSvg />
                    </div>
                    <div className="modal-unit-details-new__glance-main">
                      <FormattedMessage id="beds.count" values={{ count: bedsCount }} />
                    </div>
                    {bedsDescription && (
                      <div className="modal-unit-details-new__glance-sub">
                        {bedsDescription}
                      </div>
                    )}
                  </div>
                )}
                {(bedroomsCount > 0 || bathroomsCount > 0) && (
                  <div className="modal-unit-details-new__glance-card">
                    <div className="modal-unit-details-new__glance-icon">
                      <BathSvg />
                    </div>
                    <div className="modal-unit-details-new__glance-main">
                      {bedroomsCount} / {bathroomsCount}
                    </div>
                    <div className="modal-unit-details-new__glance-sub">
                      <FormattedMessage id="bed.bath" />
                    </div>
                  </div>
                )}
                <div className="modal-unit-details-new__glance-card">
                  <div className="modal-unit-details-new__glance-icon">
                    <InfoSvg />
                  </div>
                  <div className="modal-unit-details-new__glance-main">
                    <FormattedMessage id="unit.tbd" />
                  </div>
                  <div className="modal-unit-details-new__glance-sub">
                    <FormattedMessage id="at.check.in" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {hasAmenities && (
            <div className="modal-unit-details-new__section">
              <h5 className="modal-unit-details-new__section-title">
                <FormattedMessage id="tab.amenities" />
              </h5>
              <div className="modal-unit-details-new__amenities">
                {allAmenities.map((a, i) => (
                  <div key={`amenity-${i}`} className="modal-unit-details-new__amenity">
                    <CheckMarkSvg />
                    <span>{a}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasPriceBreakdown && (
            <div className="modal-unit-details-new__section">
              <h5 className="modal-unit-details-new__section-title">
                <FormattedMessage id="price.breakdown" />
              </h5>
              <div className="modal-unit-details-new__price-box">
                {computedPricePerNight > 0 && (
                  <div className="modal-unit-details-new__price-line">
                    <span>
                      <FormattedMessage id="nights" values={{ count: nights }} /> ×{' '}
                      <Currency currency={currency} />
                      {renderNumber(computedPricePerNight, 2)}
                    </span>
                    <span>
                      <Currency currency={currency} />
                      {renderNumber(computedPricePerNight * nights, 2)}
                    </span>
                  </div>
                )}
                {taxesAndFees > 0 && (
                  <div className="modal-unit-details-new__price-line">
                    <span>
                      <FormattedMessage id="taxes.and.fees" />
                    </span>
                    <span>
                      <Currency currency={currency} />
                      {renderNumber(taxesAndFees, 2)}
                    </span>
                  </div>
                )}
                {!!savings && savings > 0 && (
                  <>
                    <div className="modal-unit-details-new__price-line discount">
                      <span>
                        <FormattedMessage id="member.discount" />
                        {savingsPercent ? ` (${savingsPercent}%)` : ''}
                      </span>
                      <span>
                        -<Currency currency={currency} />
                        {renderNumber(savings, 2)}
                      </span>
                    </div>
                    <div className="modal-unit-details-new__price-strike">
                      <Currency currency={currency} />
                      {publicPrice
                        ? renderNumber(publicPrice, 2)
                        : renderNumber(price + savings, 2)}
                    </div>
                  </>
                )}
                <div className="modal-unit-details-new__price-divider" />
                <div className="modal-unit-details-new__price-total">
                  <span>
                    <FormattedMessage id="total" />
                  </span>
                  <span>
                    <Currency currency={currency} />
                    {renderNumber(price, 2)}
                  </span>
                </div>
              </div>
            </div>
          )}

          {hasKBYG && (
            <div className="modal-unit-details-new__section">
              <h5 className="modal-unit-details-new__section-title">
                <FormattedMessage id="know.before.you.go" />
              </h5>
              <div className="modal-unit-details-new__kbug">
                <InfoSvg />
                <div>
                  {knowBeforeYouGoMessages.map((m, i) => (
                    <p key={`kbyg-${i}`} style={{ margin: 0 }}>{parse(m)}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          {hasFees && (
            <div className="modal-unit-details-new__section">
              <div className="modal-unit-details-new__fees-box">
                <div className="modal-unit-details-new__fees-title">
                  <FormattedMessage id="possible.additional.fees" />
                </div>
                {additionalFees.map((f, i) => (
                  <div key={`fee-${i}`} className="modal-unit-details-new__fees-row">
                    <span className="modal-unit-details-new__fees-label">{f.label}</span>
                    <span className="modal-unit-details-new__fees-value">{parse(f.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {!isEmpty(cancellationPolicyText) && (
            <div className="modal-unit-details-new__section">
              <div className="modal-unit-details-new__disclaimer">
                <ExclamationGreySvg />
                <span>{cancellationPolicyText}</span>
              </div>
            </div>
          )}

          {hasFees && (
            <div className="modal-unit-details-new__section">
              <div className="modal-unit-details-new__disclaimer">
                <ExclamationGreySvg />
                <span>
                  <FormattedMessage id="not.traditional.timeshare.disclaimer" />
                </span>
              </div>
            </div>
          )}
        </div>

        <div className="modal-unit-details-new__footer">
          <span className="modal-unit-details-new__footer-note">
            {isNonRefundable && !certificateApplied && (
              <>
                <InfoSvg />
                <FormattedMessage id="no.cancellations.or.refunds" />
              </>
            )}
          </span>
          <button
            className="modal-unit-details-new__footer-reserve"
            onClick={handleReserve}
          >
            <FormattedMessage id="reserve" />
          </button>
        </div>
      </div>
    </Modal>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => ({
  loginStore: state.loginStore,
});

export const ModalUnitDetailsNew = connect(mapStateToProps)(
  injectIntl(ModalUnitDetailsNewComponent),
);
